import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'
import {
  cloneDocument, computeWarnings, createImageLayer, createShapeLayer, createStickerLayer, createTextLayer, documentHash, fullBleedFrame, nextLayerId,
  normalizeDocument, rescaleDocument, swapImageSources, applyTemplate as applyTemplateFn, autoFill as autoFillFn, layoutToLayers, collageLayoutById,
  hasBlockingWarnings,
  type DesignCanvas, type DesignDocument, type DesignWarning, type ImageLayer, type Layer, type ProductGeometry, type UploadRef,
} from '@pfe/design-renderer'
import type { ResolvedEditorConfig } from '../config/editorConfig.defaults'
import { createDesign, getDesign, renameDesign, saveDesign, uploadDesignThumbnail, type DesignApiError, type DesignDocumentPayload, type DesignTemplateSummary } from '../services/designApi'
import { designDrafts } from '../utils/designDb'
import { getWidgetConfig } from '../runtime/widgetConfig'

export type SaveState = 'idle' | 'saving' | 'saved' | 'conflict' | 'offline' | 'error'

const HISTORY_LIMIT = 50

/**
 * Dokument, Auswahl, Undo/Redo (Snapshots, ≤ 50), Autosave (1,5 s Debounce + pagehide) und
 * lokaler Draft (designDB). Alle Positionen in mm; Screen-Pixel existieren nur in den Gesten.
 */
export const useDesignStore = defineStore('design', () => {
  const doc = ref<DesignDocument | null>(null)
  const sibling = ref<DesignDocument | null>(null)
  const meta = shallowRef<DesignDocumentPayload | null>(null)
  const siblingMeta = shallowRef<DesignDocumentPayload | null>(null)
  const activeSide = ref<'front' | 'back'>('front')
  const geometry = shallowRef<ProductGeometry | null>(null)
  const config = shallowRef<ResolvedEditorConfig | null>(null)
  const projectCode = ref('')

  const selection = ref<string[]>([])
  const editingTextId = ref<string | null>(null)
  const inSlotEditId = ref<string | null>(null)
  const past = shallowRef<DesignDocument[]>([])
  const future = shallowRef<DesignDocument[]>([])
  let txnSnapshot: DesignDocument | null = null

  const dirty = ref(false)
  const saveState = ref<SaveState>('idle')
  const saveError = ref<string | null>(null)
  const conflictCurrent = shallowRef<DesignDocumentPayload | null>(null)
  const uploadInfo = ref<Record<string, { widthPx: number; heightPx: number; status?: string; mime?: string }>>({})
  const availableFontIds = ref<string[]>([])
  const overflowingTextLayerIds = ref<string[]>([])
  /** Vorlagen-Editor (Admin-Widget): kein Server-Dokument, keine Autosaves, Constraints editierbar */
  const templateMode = ref(false)
  const draftAvailable = shallowRef<{ document: DesignDocument; savedAt: number } | null>(null)

  let saveTimer: ReturnType<typeof setTimeout> | null = null
  let saveInFlight: Promise<void> | null = null

  const editorBaseUrl = () => String((getWidgetConfig() as any)?.editorBaseUrl || '').trim()

  const activeDoc = computed(() => (activeSide.value === 'back' && sibling.value ? sibling.value : doc.value))
  const activeMeta = computed(() => (activeSide.value === 'back' && siblingMeta.value ? siblingMeta.value : meta.value))
  const selectedLayer = computed<Layer | null>(() => {
    const d = activeDoc.value
    const id = selection.value[0]
    return d && id ? d.layers.find((l) => l.id === id) ?? null : null
  })
  const warnings = computed<DesignWarning[]>(() => {
    const d = activeDoc.value
    if (!d) return []
    return computeWarnings(d, {
      uploads: uploadInfo.value,
      fonts: availableFontIds.value,
      limits: { minDpiWarn: config.value?.limits.minDpiWarn, minDpiBlock: config.value?.limits.minDpiBlock },
      overflowingTextLayerIds: overflowingTextLayerIds.value,
    })
  })
  const blocked = computed(() => hasBlockingWarnings(warnings.value))
  const canUndo = computed(() => past.value.length > 0)
  const canRedo = computed(() => future.value.length > 0)

  function setActiveDoc(next: DesignDocument) {
    if (activeSide.value === 'back' && sibling.value) sibling.value = next
    else doc.value = next
  }

  // ---------- Laden ----------
  async function load(input: { projectCode: string; designId?: string | null; productId?: number; categoryId?: number; sizeId?: number | null; selectedVariantIds?: Array<number | string>; sides?: Array<'front' | 'back'> }) {
    projectCode.value = input.projectCode
    reset(false)
    const base = editorBaseUrl()
    let payload: { document: DesignDocumentPayload; geometry: ProductGeometry; config: ResolvedEditorConfig }
    if (input.designId) {
      payload = await getDesign({ editorBaseUrl: base, projectCode: input.projectCode, id: input.designId })
    } else {
      const created = await createDesign({
        editorBaseUrl: base,
        projectCode: input.projectCode,
        productId: Number(input.productId || 0),
        categoryId: Number(input.categoryId || 0),
        sizeId: input.sizeId ?? null,
        selectedVariantIds: input.selectedVariantIds ?? [],
        sides: input.sides ?? ['front'],
      })
      const first = created.documents[0]
      if (!first) throw new Error('Dokument konnte nicht angelegt werden')
      payload = { document: first, geometry: created.geometry, config: created.config }
      if (created.documents[1]) {
        siblingMeta.value = created.documents[1]
        sibling.value = normalizeDocument(created.documents[1].document)
      }
    }
    meta.value = payload.document
    doc.value = normalizeDocument(payload.document.document)
    geometry.value = payload.geometry
    config.value = payload.config
    if (payload.document.linkedDocumentId && !sibling.value) {
      try {
        const linked = await getDesign({ editorBaseUrl: base, projectCode: input.projectCode, id: payload.document.linkedDocumentId })
        siblingMeta.value = linked.document
        sibling.value = normalizeDocument(linked.document.document)
      } catch { /* Rückseite optional */ }
    }
    // Lokaler Entwurf neuer als Server?
    const draft = await designDrafts.get(payload.document.id)
    if (draft && draft.serverVersion >= payload.document.version && documentHash(draft.document) !== documentHash(doc.value)) {
      draftAvailable.value = { document: draft.document, savedAt: draft.savedAt }
    }
    saveState.value = 'saved'
  }

  /**
   * Lokales Dokument ohne Server (Admin-Vorlagen-Editor). Speichern/Entwürfe sind aus,
   * der Aufrufer liest das Dokument über `activeDoc` bzw. `onChange` aus.
   */
  function loadLocal(input: { document: DesignDocument | Record<string, any>; geometry: ProductGeometry; config: ResolvedEditorConfig }) {
    reset(true)
    templateMode.value = true
    doc.value = normalizeDocument(input.document as any)
    geometry.value = input.geometry
    config.value = input.config
    saveState.value = 'idle'
  }

  /** Dokument von außen ersetzen (z. B. Größenwechsel im Admin), ohne Historie zu verlieren */
  function replaceDocument(next: DesignDocument | Record<string, any>) {
    commit(() => normalizeDocument(next as any), 'Dokument ersetzt')
  }

  function restoreDraft() {
    if (!draftAvailable.value || !doc.value) return
    commit(() => draftAvailable.value!.document, 'Entwurf wiederhergestellt')
    draftAvailable.value = null
  }

  function discardDraft() {
    if (meta.value) void designDrafts.delete(meta.value.id)
    draftAvailable.value = null
  }

  function reset(clearProject = true) {
    doc.value = null
    sibling.value = null
    meta.value = null
    siblingMeta.value = null
    activeSide.value = 'front'
    selection.value = []
    editingTextId.value = null
    inSlotEditId.value = null
    past.value = []
    future.value = []
    dirty.value = false
    saveState.value = 'idle'
    conflictCurrent.value = null
    draftAvailable.value = null
    templateMode.value = false
    if (clearProject) projectCode.value = ''
  }

  // ---------- Historie / Commits ----------
  function pushHistory(snapshot: DesignDocument) {
    const next = [...past.value, snapshot]
    if (next.length > HISTORY_LIMIT) next.shift()
    past.value = next
    future.value = []
  }

  /** Eine Änderung als Undo-Schritt anwenden. */
  function commit(mutate: (draft: DesignDocument) => DesignDocument | void, _label = ''): void {
    const current = activeDoc.value
    if (!current) return
    const before = cloneDocument(current)
    const draft = cloneDocument(current)
    const result = mutate(draft) ?? draft
    setActiveDoc(result)
    pushHistory(before)
    afterChange()
  }

  /** Geste: beginTxn() beim pointerdown, patch() beliebig oft, commitTxn() beim pointerup = ein Undo-Schritt. */
  function beginTxn() {
    txnSnapshot = activeDoc.value ? cloneDocument(activeDoc.value) : null
  }
  function commitTxn() {
    if (!txnSnapshot || !activeDoc.value) return
    if (documentHash(txnSnapshot) !== documentHash(activeDoc.value)) {
      pushHistory(txnSnapshot)
      afterChange()
    }
    txnSnapshot = null
  }
  function cancelTxn() {
    if (txnSnapshot) setActiveDoc(txnSnapshot)
    txnSnapshot = null
  }

  function patchLayer(id: string, patch: Partial<Layer> | ((layer: Layer) => Partial<Layer>)) {
    const current = activeDoc.value
    if (!current) return
    const idx = current.layers.findIndex((l) => l.id === id)
    if (idx < 0) return
    const layer = current.layers[idx]!
    const p = typeof patch === 'function' ? patch(layer) : patch
    const layers = current.layers.slice()
    layers[idx] = { ...layer, ...p } as Layer
    setActiveDoc({ ...current, layers })
  }

  function undo() {
    const current = activeDoc.value
    const prev = past.value[past.value.length - 1]
    if (!current || !prev) return
    past.value = past.value.slice(0, -1)
    future.value = [...future.value, cloneDocument(current)]
    setActiveDoc(prev)
    selection.value = selection.value.filter((id) => prev.layers.some((l) => l.id === id))
    afterChange()
  }
  function redo() {
    const current = activeDoc.value
    const next = future.value[future.value.length - 1]
    if (!current || !next) return
    future.value = future.value.slice(0, -1)
    past.value = [...past.value, cloneDocument(current)]
    setActiveDoc(next)
    afterChange()
  }

  function afterChange() {
    dirty.value = true
    saveState.value = saveState.value === 'conflict' ? 'conflict' : 'idle'
    if (templateMode.value) return
    scheduleSave()
    const m = activeMeta.value
    const d = activeDoc.value
    if (m && d) void designDrafts.put({ id: m.id, projectCode: projectCode.value, document: d, serverVersion: m.version, savedAt: Date.now() })
  }

  // ---------- Layer-Operationen ----------
  function select(ids: string[]) {
    selection.value = ids
    if (editingTextId.value && !ids.includes(editingTextId.value)) editingTextId.value = null
    if (inSlotEditId.value && !ids.includes(inSlotEditId.value)) inSlotEditId.value = null
  }

  function addLayer(layer: Layer, selectIt = true) {
    commit((d) => { d.layers.push(layer) }, 'Ebene hinzugefügt')
    if (selectIt) select([layer.id])
  }

  function newId(prefix = 'l'): string {
    return nextLayerId(prefix, (activeDoc.value?.layers ?? []).map((l) => l.id))
  }

  /** Neue Bild-Ebene; mit `at` (mm, z. B. Drop-Position) dort zentriert, Seitenverhältnis vom Foto. */
  function addImageLayer(upload?: UploadRef | null, at?: { xMm: number; yMm: number } | null) {
    const d = activeDoc.value
    if (!d) return
    const ratio = upload?.width && upload?.height ? upload.height / upload.width : 0.75
    let w = Math.min(d.canvas.widthMm, d.canvas.heightMm) * 0.5
    let h = w * ratio
    const maxH = d.canvas.heightMm * 0.8
    if (h > maxH) { w *= maxH / h; h = maxH }
    // Drop-Position so klemmen, dass das Bild vollständig auf der Fläche liegt
    const cx = at ? Math.max(w / 2, Math.min(d.canvas.widthMm - w / 2, at.xMm)) : d.canvas.widthMm / 2
    const cy = at ? Math.max(h / 2, Math.min(d.canvas.heightMm - h / 2, at.yMm)) : d.canvas.heightMm / 2
    const frame = { xMm: cx - w / 2, yMm: cy - h / 2, wMm: w, hMm: h, rotationDeg: 0 }
    const layer = createImageLayer(newId('img'), frame, upload ? { source: { kind: 'upload', uploadId: upload.uploadId, url: upload.url, width: upload.width, height: upload.height } } : {})
    addLayer(layer)
  }

  /** Leerer Bild-Slot (Vorlagen): nächster freier slotIndex */
  function addPlaceholderLayer(frame?: { xMm: number; yMm: number; wMm: number; hMm: number }) {
    const d = activeDoc.value
    if (!d) return
    const used = d.layers.filter((l) => l.type === 'image' && l.source.kind === 'placeholder').map((l) => (l.type === 'image' ? Number(l.source.slotIndex ?? 0) : 0))
    const slotIndex = used.length ? Math.max(...used) + 1 : 0
    const w = Math.min(d.canvas.widthMm, d.canvas.heightMm) * 0.5
    const f = frame ?? { xMm: (d.canvas.widthMm - w) / 2, yMm: (d.canvas.heightMm - w * 0.75) / 2, wMm: w, hMm: w * 0.75 }
    const layer = createImageLayer(newId('slot'), { ...f, rotationDeg: 0 }, { source: { kind: 'placeholder', slotIndex } })
    addLayer(layer)
  }

  /** Constraints einer Ebene (nur Vorlagen-Editor) */
  function setConstraints(id: string, patch: Partial<Layer['constraints']>) {
    commit((d) => {
      const l = d.layers.find((x) => x.id === id)
      if (l) l.constraints = { ...l.constraints, ...patch }
    }, 'Constraints')
  }

  function addTextLayer(text = 'Dein Text', familyId = '', family?: string, opts: { sizePt?: number; weight?: number; heightFactor?: number } = {}) {
    const d = activeDoc.value
    if (!d) return
    const w = d.canvas.widthMm * 0.6
    const h = Math.max(10, d.canvas.heightMm * (opts.heightFactor ?? 0.12))
    const layer = createTextLayer(newId('txt'), { xMm: (d.canvas.widthMm - w) / 2, yMm: (d.canvas.heightMm - h) / 2, wMm: w, hMm: h, rotationDeg: 0 }, {
      text,
      font: { familyId, family, weight: opts.weight ?? 700, italic: false, sizePt: opts.sizePt ?? Math.max(12, Math.round(d.canvas.heightMm / 6)), lineHeight: 1.15, letterSpacingEm: 0 },
      align: 'center',
      verticalAlign: 'middle',
    })
    addLayer(layer)
  }

  /** Sticker aus der Asset-Bibliothek mittig einfügen (Breite = Einfügebreite, höchstens 60 % der Fläche). */
  function addStickerLayer(asset: { id: string; url: string; defaultWidthMm?: number; aspectRatio?: number }) {
    const d = activeDoc.value
    if (!d || !asset?.url) return
    const ar = Number(asset.aspectRatio) > 0 ? Number(asset.aspectRatio) : 1
    let w = Math.min(Number(asset.defaultWidthMm) || 30, d.canvas.widthMm * 0.6)
    let h = w / ar
    if (h > d.canvas.heightMm * 0.6) { h = d.canvas.heightMm * 0.6; w = h * ar }
    const frame = { xMm: (d.canvas.widthMm - w) / 2, yMm: (d.canvas.heightMm - h) / 2, wMm: w, hMm: h, rotationDeg: 0 }
    const layer = createStickerLayer(newId('stk'), frame, asset.id, { source: { assetId: asset.id, url: asset.url } })
    addLayer(layer)
  }

  function addShapeLayer(shape: 'rect' | 'ellipse' | 'line' = 'rect') {
    const d = activeDoc.value
    if (!d) return
    const w = d.canvas.widthMm * 0.3
    const h = shape === 'line' ? 2 : w
    const layer = createShapeLayer(newId('shp'), { xMm: (d.canvas.widthMm - w) / 2, yMm: (d.canvas.heightMm - h) / 2, wMm: w, hMm: h, rotationDeg: 0 }, { shape, fill: '#FFFFFF' })
    addLayer(layer)
  }

  function removeLayers(ids: string[]) {
    if (!ids.length) return
    // Gesperrte Vorlagen-Ebenen bleiben erhalten.
    commit((d) => {
      // Feste Bogen-Plätze (contentOnly) bleiben, nur das Foto wird herausgenommen
      for (const l of d.layers) {
        if (ids.includes(l.id) && l.type === 'image' && l.constraints.contentOnly && d.meta.sheet) {
          l.source = { kind: 'placeholder' }
          l.fit = { mode: 'cover', offsetX: 0.5, offsetY: 0.5, scale: 1 }
        }
      }
      d.layers = d.layers.filter((l) => !ids.includes(l.id) || l.constraints.locked || (l.type === 'image' && l.constraints.contentOnly && !!d.meta.sheet))
    }, 'Ebene gelöscht')
    select([])
  }

  function duplicateLayer(id: string) {
    const d = activeDoc.value
    const layer = d?.layers.find((l) => l.id === id)
    if (!d || !layer) return
    const copy = { ...cloneDocument(layer), id: newId(layer.type.slice(0, 3)), frame: { ...layer.frame, xMm: layer.frame.xMm + 5, yMm: layer.frame.yMm + 5 } } as Layer
    addLayer(copy)
  }

  function reorder(id: string, direction: 'up' | 'down' | 'top' | 'bottom') {
    commit((d) => {
      const idx = d.layers.findIndex((l) => l.id === id)
      if (idx < 0) return
      const [layer] = d.layers.splice(idx, 1)
      const target = direction === 'top' ? d.layers.length : direction === 'bottom' ? 0 : direction === 'up' ? Math.min(d.layers.length, idx + 1) : Math.max(0, idx - 1)
      d.layers.splice(target, 0, layer!)
    }, 'Reihenfolge')
  }

  function setBackground(background: DesignCanvas['background']) {
    commit((d) => { d.canvas.background = background }, 'Hintergrund')
  }

  function setCanvasSize(nextCanvas: DesignCanvas, template?: { id: string; version?: number; document: DesignDocument } | null) {
    const d = activeDoc.value
    if (!d) return
    commit(() => {
      if (template && d.meta.templateId === template.id) {
        const uploads = uploadsFromDocument(d)
        return applyTemplateFn(template, nextCanvas, uploads)
      }
      return rescaleDocument(d, nextCanvas)
    }, 'Format geändert')
  }

  function applyTemplate(template: DesignTemplateSummary & { document: DesignDocument }, uploads: UploadRef[] = []) {
    const d = activeDoc.value
    if (!d) return
    const pool = uploads.length ? uploads : uploadsFromDocument(d)
    commit(() => applyTemplateFn({ id: template.id, version: template.version, document: normalizeDocument(template.document) }, d.canvas, pool), 'Vorlage angewendet')
    select([])
  }

  function applyLayout(layoutId: string) {
    const d = activeDoc.value
    const layout = collageLayoutById(layoutId)
    if (!d || !layout) return
    const gutter = config.value?.canvas.collageGutterMm ?? 3
    commit((draft) => {
      const sources = uploadsFromDocument(draft)
      const keep = draft.layers.filter((l) => l.type !== 'image')
      const slots = layoutToLayers(layout, draft.canvas, gutter)
      draft.layers = [...slots, ...keep]
      draft.meta.templateId = null
      return autoFillFn(draft, sources)
    }, 'Layout')
    select([])
  }

  /**
   * Bogen anwenden: Format und Fotoplätze kommen 1:1 vom Bogen (keine Skalierung), Plätze sind
   * fest (contentOnly), alles andere gesperrt. Vorhandene Fotos wandern der Reihe nach in die Plätze.
   */
  function applySheet(sheet: { id: string; version?: number; name: string; document: DesignDocument }) {
    const d = activeDoc.value
    if (!d) return
    const pool = uploadsFromDocument(d)
    const fits = new Map<string, ImageLayer['fit']>()
    for (const l of d.layers) if (l.type === 'image' && l.source.kind === 'upload') fits.set(l.source.uploadId, l.fit)
    commit(() => {
      const src = normalizeDocument(sheet.document)
      const next: DesignDocument = {
        schemaVersion: 1,
        canvas: { ...src.canvas },
        layers: src.layers.map((l) => ({
          ...l,
          constraints: l.type === 'image'
            ? { ...l.constraints, locked: false, contentOnly: true, optional: false, role: 'slot' }
            : { ...l.constraints, locked: true, contentOnly: false },
          locked: l.type !== 'image',
        })) as Layer[],
        meta: { templateId: sheet.id, templateVersion: sheet.version ?? null, generator: 'pfe-editor/sheet', sheet: { templateId: sheet.id, name: sheet.name } },
      }
      const filled = autoFillFn(next, pool)
      // Ausschnitt eines Fotos beibehalten, wenn es im neuen Bogen gleich große Plätze bekommt
      for (const l of filled.layers) {
        if (l.type === 'image' && l.source.kind === 'upload') {
          const prev = fits.get(l.source.uploadId)
          if (prev) l.fit = { ...prev }
        }
      }
      return filled
    }, 'Bogen gewählt')
    select([])
  }

  /** Ein Foto in alle (leeren) Plätze setzen – z. B. 16× dasselbe Motiv beim Fotosticker */
  function fillSlots(upload: UploadRef, onlyEmpty = true) {
    commit((d) => {
      for (const l of d.layers) {
        if (l.type !== 'image' || (onlyEmpty && l.source.kind !== 'placeholder')) continue
        l.source = { kind: 'upload', uploadId: upload.uploadId, url: upload.url, width: upload.width, height: upload.height }
        l.fit = { mode: 'cover', offsetX: 0.5, offsetY: 0.5, scale: 1 }
      }
    }, 'Plätze gefüllt')
  }

  /** Foto aus einem festen Platz nehmen (Platz bleibt) */
  function clearSlot(layerId: string) {
    commit((d) => {
      const l = d.layers.find((x) => x.id === layerId)
      if (l && l.type === 'image') {
        l.source = { kind: 'placeholder' }
        l.fit = { mode: 'cover', offsetX: 0.5, offsetY: 0.5, scale: 1 }
      }
    }, 'Foto entfernt')
  }

  function autoFill(uploads: UploadRef[]) {
    const d = activeDoc.value
    if (!d) return
    commit(() => autoFillFn(d, uploads), 'Automatisch gefüllt')
  }

  function swapSlots(aId: string, bId: string) {
    const d = activeDoc.value
    if (!d) return
    commit(() => swapImageSources(d, aId, bId), 'Bilder getauscht')
  }

  function setImageSource(layerId: string, upload: UploadRef) {
    commit((d) => {
      const l = d.layers.find((x) => x.id === layerId)
      if (l && l.type === 'image') {
        l.source = { kind: 'upload', uploadId: upload.uploadId, url: upload.url, width: upload.width, height: upload.height }
        l.fit = { mode: 'cover', offsetX: 0.5, offsetY: 0.5, scale: 1 }
      }
    }, 'Bild eingesetzt')
  }

  function uploadsFromDocument(d: DesignDocument): UploadRef[] {
    const out: UploadRef[] = []
    for (const l of d.layers) {
      if (l.type === 'image' && l.source.kind === 'upload') out.push({ uploadId: l.source.uploadId, url: l.source.url, width: l.source.width, height: l.source.height })
    }
    return out
  }

  function fullBleedLayerFrame() {
    const d = activeDoc.value
    return d ? fullBleedFrame(d.canvas) : null
  }

  // ---------- Speichern ----------
  // ---------- Lokale Vorschau beim Drag & Drop ----------
  // Frisch gezogene Dateien stehen sofort mit einer blob:-Vorschau auf der Fläche. Gespeichert
  // wird erst, wenn der Upload fertig ist und die blob:-URL durch die Server-URL ersetzt wurde.
  let localUploadResolver: ((uploadId: string) => UploadRef | null) | null = null
  function setLocalUploadResolver(fn: ((uploadId: string) => UploadRef | null) | null) { localUploadResolver = fn }
  const isLocalUrl = (url: string | null | undefined) => !!url && url.startsWith('blob:')
  function resolveLocalSources(d: DesignDocument): { next: DesignDocument; changed: boolean; pending: boolean } {
    let changed = false
    let pending = false
    const layers = d.layers.map((l) => {
      if (l.type !== 'image' || l.source.kind !== 'upload' || !isLocalUrl(l.source.url)) return l
      const ref = localUploadResolver?.(l.source.uploadId) ?? null
      if (!ref || isLocalUrl(ref.url)) { pending = true; return l }
      changed = true
      return { ...l, source: { ...l.source, url: ref.url, width: ref.width ?? l.source.width, height: ref.height ?? l.source.height } } as Layer
    })
    return { next: changed ? { ...d, layers } : d, changed, pending }
  }
  /** Anzahl Bilder der aktiven Seite, die noch hochladen (blob:-Vorschau). */
  const pendingLocalUploads = computed(() => (activeDoc.value?.layers ?? []).filter((l) => l.type === 'image' && l.source.kind === 'upload' && isLocalUrl(l.source.url)).length)
  /** blob:-Vorschauen durch Server-URLs ersetzen (ohne Undo-Schritt); true, wenn noch etwas aussteht. */
  function swapLocalSources(): boolean {
    let pending = false
    let activeChanged = false
    for (const side of ['front', 'back'] as const) {
      const target = side === 'back' ? sibling : doc
      if (!target.value) continue
      const res = resolveLocalSources(target.value)
      pending = pending || res.pending
      if (!res.changed) continue
      target.value = res.next
      if ((activeSide.value === 'back') === (side === 'back')) activeChanged = true
    }
    if (activeChanged && !templateMode.value) {
      dirty.value = true
      scheduleSave(300)
    }
    return pending
  }

  function scheduleSave(delayMs = 1500) {
    if (saveTimer) clearTimeout(saveTimer)
    saveTimer = setTimeout(() => { void save() }, delayMs)
  }

  async function save(): Promise<void> {
    if (saveInFlight) { await saveInFlight; if (!dirty.value) return }
    // Noch nicht fertig hochgeladene Bilder (blob:-Vorschau) nie zum Server schicken.
    if (swapLocalSources() && activeDoc.value && resolveLocalSources(activeDoc.value).pending) {
      saveState.value = 'saving'
      scheduleSave(2000)
      return
    }
    const m = activeMeta.value
    const d = activeDoc.value
    if (!m || !d || !dirty.value) return
    if (m.status === 'ordered') return
    saveState.value = 'saving'
    const run = (async () => {
      try {
        const res = await saveDesign({ editorBaseUrl: editorBaseUrl(), projectCode: projectCode.value, id: m.id, version: m.version, document: d })
        const updated = { ...m, version: res.version, documentHash: res.documentHash, uploadIds: res.uploadIds }
        if (activeSide.value === 'back' && siblingMeta.value) siblingMeta.value = updated
        else meta.value = updated
        dirty.value = false
        saveState.value = 'saved'
        saveError.value = null
        void designDrafts.delete(m.id)
      } catch (e) {
        const err = e as DesignApiError
        if (err?.status === 409) {
          saveState.value = 'conflict'
          conflictCurrent.value = err.current ?? null
        } else if (err?.status === 422) {
          saveState.value = 'error'
          saveError.value = (err.errors ?? []).map((x) => x.message).join('; ') || 'Dokument ungültig'
        } else if (!err?.status) {
          saveState.value = 'offline'
        } else {
          saveState.value = 'error'
          saveError.value = err.error || 'Speichern fehlgeschlagen'
        }
      } finally {
        saveInFlight = null
      }
    })()
    saveInFlight = run
    await run
  }

  /** Konflikt: Serverstand übernehmen (eigene Änderungen verwerfen) */
  function adoptServerVersion() {
    const cur = conflictCurrent.value
    if (!cur) return
    if (activeSide.value === 'back' && siblingMeta.value) { siblingMeta.value = cur; sibling.value = normalizeDocument(cur.document) }
    else { meta.value = cur; doc.value = normalizeDocument(cur.document) }
    conflictCurrent.value = null
    dirty.value = false
    saveState.value = 'saved'
    past.value = []
    future.value = []
  }

  /** Konflikt: eigenen Stand über den Serverstand schreiben */
  async function overwriteServerVersion() {
    const cur = conflictCurrent.value
    if (!cur) return
    if (activeSide.value === 'back' && siblingMeta.value) siblingMeta.value = { ...siblingMeta.value, version: cur.version }
    else if (meta.value) meta.value = { ...meta.value, version: cur.version }
    conflictCurrent.value = null
    dirty.value = true
    await save()
  }

  async function flushSave() {
    if (saveTimer) { clearTimeout(saveTimer); saveTimer = null }
    await save()
  }

  async function pushThumbnail(dataUrl: string) {
    const m = activeMeta.value
    if (!m) return null
    try {
      const res = await uploadDesignThumbnail({ editorBaseUrl: editorBaseUrl(), projectCode: projectCode.value, id: m.id, dataUrl })
      return res.thumbnailUrl
    } catch {
      return null
    }
  }

  /** Name des Designs (Vorderseite = maßgeblich; Rückseite bekommt serverseitig denselben). */
  const designName = computed(() => String(meta.value?.name || '').trim())
  async function rename(nameRaw: string): Promise<void> {
    const m = meta.value
    if (!m || templateMode.value) return
    const name = nameRaw.replace(/\s+/g, ' ').trim().slice(0, 120) || null
    if ((m.name || null) === name) return
    const cfg = getWidgetConfig() as any
    const res = await renameDesign({ editorBaseUrl: cfg.editorBaseUrl, projectCode: projectCode.value, id: m.id, name })
    meta.value = { ...m, name: res.name }
    if (siblingMeta.value) siblingMeta.value = { ...siblingMeta.value, name: res.name }
  }

  function setUploadInfo(map: Record<string, { widthPx: number; heightPx: number; status?: string; mime?: string }>) {
    uploadInfo.value = { ...uploadInfo.value, ...map }
  }

  return {
    doc, sibling, meta, siblingMeta, activeSide, activeDoc, activeMeta, geometry, config, projectCode,
    selection, selectedLayer, editingTextId, inSlotEditId, warnings, blocked, canUndo, canRedo,
    dirty, saveState, saveError, conflictCurrent, draftAvailable, availableFontIds, overflowingTextLayerIds, templateMode,
    load, loadLocal, replaceDocument, addPlaceholderLayer, setConstraints, reset, restoreDraft, discardDraft,
    commit, beginTxn, commitTxn, cancelTxn, patchLayer, undo, redo,
    select, addLayer, addImageLayer, addTextLayer, addShapeLayer, addStickerLayer, removeLayers, duplicateLayer, reorder,
    setBackground, setCanvasSize, applyTemplate, applyLayout, autoFill, swapSlots, setImageSource, fullBleedLayerFrame, uploadsFromDocument,
    applySheet, fillSlots, clearSlot,
    save, flushSave, adoptServerVersion, overwriteServerVersion, pushThumbnail, setUploadInfo, newId,
    designName, rename, setLocalUploadResolver, swapLocalSources, pendingLocalUploads,
  }
})
